import { calculateDcfValuation, type DcfValuationInput, type DcfHistoricalYear, type DcfProjectionAssumptions, type DcfWaccInputs } from "./calculate";

/**
 * Monte Carlo DCF simulation.
 * Runs N iterations with randomized parameters drawn from distributions
 * around the base-case assumptions to produce a probability distribution
 * of enterprise values (P10, P25, P50, P75, P90).
 */

export interface MonteCarloInput {
  historicalYears: DcfHistoricalYear[];
  baseAssumptions: DcfProjectionAssumptions[];
  waccInputs: DcfWaccInputs;
  iterations?: number;
  /**
   * Deterministic seed. When provided, identical inputs produce identical
   * percentiles across runs (replicability). Omit for non-deterministic runs.
   */
  seed?: number;
}

export interface MonteCarloResult {
  p10: number;
  p25: number;
  p50: number;
  p75: number;
  p90: number;
  mean: number;
  stdDev: number;
  iterations: number;
}

type Rng = () => number;

/**
 * mulberry32 — small, fast, deterministic 32-bit PRNG. Quality is more than
 * sufficient for Monte Carlo sampling and identical seeds replay identically.
 */
export function mulberry32(seed: number): Rng {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Stable 32-bit hash for deriving a Monte Carlo seed from a report ID. */
export function hashStringToSeed(value: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < value.length; i++) {
    h ^= value.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function triangularSample(rng: Rng, min: number, mode: number, max: number): number {
  const u = rng();
  const fc = (mode - min) / (max - min);
  if (u < fc) {
    return min + Math.sqrt(u * (max - min) * (mode - min));
  }
  return max - Math.sqrt((1 - u) * (max - min) * (max - mode));
}

function normalSample(rng: Rng, mean: number, stdDev: number): number {
  // Box-Muller transform; guard u1 > 0 so log() stays finite.
  const u1 = Math.max(rng(), Number.EPSILON);
  const u2 = rng();
  const z = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
  return mean + z * stdDev;
}

function percentile(sorted: number[], p: number): number {
  const idx = (p / 100) * (sorted.length - 1);
  const lower = Math.floor(idx);
  const upper = Math.ceil(idx);
  if (lower === upper) return sorted[lower];
  return sorted[lower] + (sorted[upper] - sorted[lower]) * (idx - lower);
}

/**
 * Run Monte Carlo simulation for DCF valuation.
 * Default: 5000 iterations. Revenue growth sampled from triangular distribution,
 * operating margin from normal distribution, WACC from uniform +/-2%.
 */
export function monteCarloSimulation(input: MonteCarloInput): MonteCarloResult {
  const n = input.iterations ?? 5000;
  const rng: Rng = input.seed != null ? mulberry32(input.seed) : Math.random;
  const results: number[] = [];

  const baseGrowth = input.baseAssumptions[0]?.revenueGrowthPct ?? 0.03;
  const baseMargin = input.baseAssumptions[0]?.operatingMarginPct ?? 0.10;
  const baseCapex = input.baseAssumptions[0]?.capexPct ?? 0.02;
  const baseNwc = input.baseAssumptions[0]?.nwcPct ?? 0.01;

  for (let i = 0; i < n; i++) {
    // Sample revenue growth from triangular: (base*0.3, base, base*1.8)
    const growthMin = Math.max(baseGrowth * 0.3, -0.05);
    const growthMax = Math.min(baseGrowth * 1.8, 0.20);
    const sampledGrowth = triangularSample(rng, growthMin, baseGrowth, growthMax);

    // Sample operating margin from normal: mean=base, stdDev=base*0.2
    const marginStd = Math.max(baseMargin * 0.20, 0.01);
    const sampledMargin = Math.max(normalSample(rng, baseMargin, marginStd), 0.01);

    // WACC perturbation: uniform +/- 2%
    const waccDelta = (rng() - 0.5) * 0.04;

    // Build assumptions with sampled parameters, decaying over projection years
    const assumptions: DcfProjectionAssumptions[] = input.baseAssumptions.map((a, yr) => ({
      ...a,
      revenueGrowthPct: sampledGrowth * (1 - yr * 0.08),
      operatingMarginPct: sampledMargin,
      capexPct: baseCapex,
      nwcPct: baseNwc,
    }));

    try {
      const dcfInput: DcfValuationInput = {
        historicalYears: input.historicalYears,
        projectionAssumptions: assumptions,
        waccInputs: input.waccInputs,
        _waccOverride: undefined,
        skipValidation: true,
      };

      // Apply WACC delta via override if we could compute base WACC
      // For simplicity, we pass the delta as an override hint
      (dcfInput as any)._waccDelta = waccDelta;

      const result = calculateDcfValuation(dcfInput);
      if (result.enterpriseValue > 0 && isFinite(result.enterpriseValue)) {
        results.push(result.enterpriseValue);
      }
    } catch {
      // Skip failed iterations (bad parameter combinations)
    }
  }

  if (results.length < 100) {
    // Not enough valid iterations — return degenerate result
    return { p10: 0, p25: 0, p50: 0, p75: 0, p90: 0, mean: 0, stdDev: 0, iterations: results.length };
  }

  results.sort((a, b) => a - b);

  const mean = results.reduce((s, v) => s + v, 0) / results.length;
  const variance = results.reduce((s, v) => s + (v - mean) ** 2, 0) / results.length;
  const stdDev = Math.sqrt(variance);

  return {
    p10: Math.round(percentile(results, 10)),
    p25: Math.round(percentile(results, 25)),
    p50: Math.round(percentile(results, 50)),
    p75: Math.round(percentile(results, 75)),
    p90: Math.round(percentile(results, 90)),
    mean: Math.round(mean),
    stdDev: Math.round(stdDev),
    iterations: results.length,
  };
}
